import { useEffect, useState } from 'react';

import type { FaxTransmissionStatusSchema } from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';

interface UseRequestFaxStatusOptions {
  matterId: string;
  providerId: string;
  requestId: string;
  enabled?: boolean;
  onStatusChange?: (
    status: FaxTransmissionStatusSchema | null | undefined,
  ) => void;
}

const POLLING_INTERVAL_MS = 180000; // 3 minutes (180 seconds)

export const useRequestFaxStatus = ({
  matterId,
  providerId,
  requestId,
  enabled = true,
  onStatusChange,
}: UseRequestFaxStatusOptions) => {
  const [isPolling, setIsPolling] = useState(false);
  const [prevStatus, setPrevStatus] = useState<string | null | undefined>(null);

  const {
    data: request,
    error,
    isLoading,
    refetch,
  } = treatmentRecordRequestService.useMattersProvidersRecordRequestsRetrieveQuery(
    { matterId, providerId, requestId },
    {
      skip: !enabled,
      pollingInterval: isPolling ? POLLING_INTERVAL_MS : undefined,
      refetchOnMountOrArgChange: true,
    },
  );

  const faxStatus = request?.fax_transmission_status;

  // Start polling when fax transmission begins
  const startPolling = () => {
    setIsPolling(true);
  };

  // Stop polling when fax reaches terminal state or manually stopped
  const stopPolling = () => {
    setIsPolling(false);
  };

  // Check if fax status is in a terminal state
  const isTerminalStatus = (status?: string | null) => {
    return status === 'sent' || status === 'delivered' || status === 'failed';
  };

  // Handle status changes
  useEffect(() => {
    const currentStatus = faxStatus?.status;

    // If status changed, notify callback
    if (currentStatus !== prevStatus) {
      onStatusChange?.(faxStatus);
      setPrevStatus(currentStatus);
    }

    // Auto-start polling if fax is pending
    if (currentStatus === 'pending' && !isPolling) {
      startPolling();
    }

    // Auto-stop polling if fax reaches terminal state
    if (currentStatus && isTerminalStatus(currentStatus) && isPolling) {
      stopPolling();
    }
  }, [faxStatus, prevStatus, isPolling, onStatusChange]);

  // Handle page visibility to pause/resume polling
  useEffect(() => {
    if (!isPolling) return undefined;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden, polling will continue but less frequently
        // RTK Query handles this automatically
      } else {
        // Page is visible, ensure we have latest data
        refetch();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isPolling, refetch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  return {
    faxStatus,
    request,
    isLoading,
    error,
    isPolling,
    startPolling,
    stopPolling,
    refetch,
    isTerminalStatus: isTerminalStatus(faxStatus?.status),
  };
};
